import { Command } from 'commander';
import path from 'path';
import os from 'os';
import fs from 'fs-extra';
import chalk from 'chalk';
import { execSync } from 'child_process';

import { scaffold, ScaffoldOptions } from '@stackwright/cli';
import { runScaffoldHooks } from '@stackwright/scaffold-core';
import { registerProScaffoldHooks } from '@stackwright-pro/scaffold-hooks';
import {
  addProDepsToPackageJson,
  addAuthToStackwrightYml,
  scaffoldWorkspaceConfig,
  addIntegrationToStackwrightYml,
  generateReadme,
} from './scaffold.js';
// Register Pro hooks (adds deps, MCP config, etc.)
// Must be called before scaffold() to ensure hooks run in the CLI's scaffold-core context
registerProScaffoldHooks();

const { version } = require('../package.json') as { version: string };

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LaunchProOptions {
  name?: string;
  title?: string;
  theme?: string;
  force?: boolean;
  skipOtters?: boolean;
  skipInstall?: boolean;
  yes?: boolean;
  spec?: string[];
  specName?: string;
  mock?: boolean;
}

// ---------------------------------------------------------------------------
// Template copying (Pro-specific - hooks can't handle these)
// ---------------------------------------------------------------------------

async function copyProTemplate(templateName: string, destPath: string): Promise<void> {
  const src = path.resolve(__dirname, '..', 'templates', 'pro', templateName);
  await fs.copy(src, destPath);
}

// ---------------------------------------------------------------------------
// Spec handling (Pro-specific - hooks can't handle these)
// ---------------------------------------------------------------------------

async function handleSpec(
  targetDir: string,
  specPath: string,
  specName?: string
): Promise<{ specFilename: string; derivedName: string }> {
  const resolvedSpec = path.resolve(specPath);

  if (!fs.existsSync(resolvedSpec)) {
    throw new Error(`Spec file not found: ${resolvedSpec}`);
  }

  const specFilename = path.basename(resolvedSpec);
  const derivedName = specName || path.basename(specFilename, path.extname(specFilename));

  const specsDir = path.join(targetDir, 'specs');
  await fs.ensureDir(specsDir);
  await fs.copy(resolvedSpec, path.join(specsDir, specFilename));

  return { specFilename, derivedName };
}

// ---------------------------------------------------------------------------
// Main launch flow
// ---------------------------------------------------------------------------

async function launch(targetDir: string, options: LaunchProOptions): Promise<void> {
  const dirName = path.basename(targetDir);

  console.log(chalk.cyan.bold('\n🚢 Launching Stackwright Pro...\n'));

  // ------------------------------------------------------------------
  // 1. Scaffold base OSS project via @stackwright/cli
  //    Hooks auto-handle: Pro deps, MCP config, role-based scripts
  // ------------------------------------------------------------------

  const scaffoldOpts: ScaffoldOptions = {
    name: options.name || dirName,
    standalone: true, // prevents `workspace:*` refs when CLI runs inside the Pro monorepo
    ...(options.title !== undefined && { title: options.title }),
    ...(options.theme !== undefined && { theme: options.theme }),
    ...(options.force !== undefined && { force: options.force }),
    ...(options.yes !== undefined && { noInteractive: options.yes }),
  };

  await scaffold(targetDir, scaffoldOpts);
  console.log(chalk.green('✅ Base project scaffolded (hooks added Pro deps + MCP config)'));

  // ------------------------------------------------------------------
  // 1b. Run pnpm install — citizen devs shouldn't need to know this step exists
  // ------------------------------------------------------------------

  // Patch Pro packages directly into package.json before install.
  // This bypasses the scaffold hooks system (which has a known version-resolution
  // issue) and guarantees pro deps land regardless of hook execution order.
  await addProDepsToPackageJson(targetDir);
  console.log(chalk.green('🦦 Pro packages added to package.json'));

  // Verify package.json has Pro deps before installing
  const verifyPkg = JSON.parse(
    await fs.readFile(path.join(targetDir, 'package.json'), 'utf-8')
  ) as Record<string, unknown>;
  const proDepCount = Object.keys(
    typeof verifyPkg.dependencies === 'object' && verifyPkg.dependencies !== null
      ? (verifyPkg.dependencies as Record<string, string>)
      : {}
  ).filter((k) => k.startsWith('@stackwright-pro')).length;
  console.log(chalk.dim(`  → package.json verified: ${proDepCount} @stackwright-pro/* deps ready`));

  console.log(chalk.cyan('\n📦 Installing dependencies (this takes a moment)...'));
  if (!options.skipInstall) {
    try {
      execSync('pnpm install --ignore-workspace', {
        cwd: targetDir,
        stdio: 'inherit',
        timeout: 120_000,
      });
      console.log(chalk.green('✅ Dependencies installed'));
    } catch (err: unknown) {
      const isTimeout =
        err instanceof Error &&
        (err.message.includes('ETIMEDOUT') || err.message.includes('timeout'));
      console.error(
        chalk.red('\n❌ pnpm install failed. Auth and Pro packages are NOT installed.')
      );
      console.error(
        chalk.red('   Your project is in an incomplete state — do NOT run or deploy it.')
      );
      if (isTimeout) {
        console.error(
          chalk.yellow('   The install timed out (>2 min). Check your network connection.')
        );
      }
      console.error(
        chalk.yellow(`   Fix the error above, then run: pnpm install --ignore-workspace`)
      );
      process.exit(1);
    }
  } else {
    console.log(chalk.dim('ℹ️  Skipping install (--skip-install). Run: pnpm install'));
  }

  // Fire postInstall hooks now that install has run
  const dependencyMode: 'workspace' | 'standalone' = 'standalone';
  await runScaffoldHooks('postInstall', {
    targetDir,
    projectName: options.name || path.basename(targetDir),
    siteTitle: options.title || options.name || path.basename(targetDir),
    themeId: options.theme || 'corporate',
    packageJson: {},
    dependencyMode,
  });

  // ------------------------------------------------------------------
  // 2. Copy Pro templates (hooks can't handle these)
  // ------------------------------------------------------------------

  // Replace providers.tsx with Pro version (AuthProvider, Pro component registration)
  // Pro integrates into the App Router layout — NOT the Pages Router _app.tsx.
  // Putting _app.tsx in pages/ triggers Next.js 16 Pages Router mode which fails
  // on missing _document (see swp-srp / GH scaffold smoke test).
  await copyProTemplate(
    'pro-providers.tsx',
    path.join(targetDir, 'app', '_components', 'providers.tsx')
  );

  // Add root content.yml for the home page
  // Goes in pages/ for Stackwright's content system — this is a YAML data file,
  // not a Next.js page module, so it does NOT trigger Pages Router mode.
  await copyProTemplate('content.yml', path.join(targetDir, 'pages', 'content.yml'));

  // Replace next.config.js with Pro version (transpile pro pkgs)
  await copyProTemplate('next.config.js', path.join(targetDir, 'next.config.js'));

  // Add lib/mock-auth.ts
  await fs.ensureDir(path.join(targetDir, 'lib'));
  await copyProTemplate('mock-auth.ts', path.join(targetDir, 'lib', 'mock-auth.ts'));

  // Add yaml.d.ts for TypeScript support
  await copyProTemplate('yaml.d.ts', path.join(targetDir, 'yaml.d.ts'));

  // Add scripts/prebuild.js + scripts/pro-content-plugin.js + scripts/start-mocks.js
  await fs.ensureDir(path.join(targetDir, 'scripts'));
  await copyProTemplate('prebuild.js', path.join(targetDir, 'scripts', 'prebuild.js'));
  await copyProTemplate(
    'scripts/pro-content-plugin.js',
    path.join(targetDir, 'scripts', 'pro-content-plugin.js')
  );
  await copyProTemplate(
    'scripts/start-mocks.js',
    path.join(targetDir, 'scripts', 'start-mocks.js')
  );

  // Override app/[...slug]/page.tsx with Pro version that filters _-prefixed data-sink slugs.
  // The OSS scaffold's generateStackwrightStaticParams returns _auth.json, _collections.json,
  // etc. as routes (they're in public/stackwright-content/ but not in RESERVED_FILES).
  // The Pro version wraps that function to filter them out.
  await fs.ensureDir(path.join(targetDir, 'app', '[...slug]'));
  await copyProTemplate(
    'app/slug-page/page.tsx',
    path.join(targetDir, 'app', '[...slug]', 'page.tsx')
  );

  console.log(chalk.green(' Auth integration added (App Router providers + RBAC)'));

  // ------------------------------------------------------------------
  // 3. Add auth section to stackwright.yml
  // ------------------------------------------------------------------

  await addAuthToStackwrightYml(targetDir);

  // ------------------------------------------------------------------
  // 4. Handle --spec if provided
  // ------------------------------------------------------------------

  let specInfo: { specFilename: string; derivedName: string } | null = null;
  const MOCK_URL = 'http://localhost:4010';

  if (options.spec && options.spec.length > 0) {
    for (const specPath of options.spec) {
      specInfo = await handleSpec(targetDir, specPath, options.specName);
      await addIntegrationToStackwrightYml(
        targetDir,
        specInfo.derivedName,
        specInfo.specFilename,
        options.mock ? MOCK_URL : undefined
      );
    }
    console.log(chalk.green(`📡 ${options.spec.length} OpenAPI integration(s) wired up`));
    if (options.mock) {
      console.log(chalk.green('🎭 Prism mock server configured'));
    }
  } else if (options.mock) {
    // --mock without --spec: use sample Petstore spec for demo
    console.log(
      chalk.yellow(
        '⚠️  No spec provided with --mock. Using sample Petstore spec for demo. Replace with your API spec.'
      )
    );

    const specsDir = path.join(targetDir, 'specs');
    await fs.ensureDir(specsDir);
    await copyProTemplate('petstore.yaml', path.join(specsDir, 'petstore.yaml'));

    specInfo = { specFilename: 'petstore.yaml', derivedName: 'petstore' };

    await addIntegrationToStackwrightYml(
      targetDir,
      specInfo.derivedName,
      specInfo.specFilename,
      MOCK_URL
    );
    console.log(chalk.green('📡 Sample Petstore spec wired up'));
    console.log(chalk.green('🎭 Prism mock server configured'));
  }

  // ------------------------------------------------------------------
  // 5. Generate README
  // ------------------------------------------------------------------

  const readmeContent = generateReadme({
    projectName: options.name || dirName,
    hasSpec: !!specInfo,
    ...(specInfo?.derivedName !== undefined && { specName: specInfo.derivedName }),
    ...(specInfo?.specFilename !== undefined && { specFilename: specInfo.specFilename }),
    hasOtters: !options.skipOtters,
    hasMock: !!options.mock,
  });

  await fs.writeFile(path.join(targetDir, 'README.md'), readmeContent);
  console.log(chalk.green('📄 README.md created'));

  // ------------------------------------------------------------------
  // 5b. Write init-context.json for raft launcher
  // ------------------------------------------------------------------

  const initContext: Record<string, unknown> = {
    projectRoot: targetDir,
    projectName: options.name || dirName,
    generatedBy: 'launch-stackwright-pro',
    version: '1.0',
  };

  if (specInfo) {
    initContext.specPath = `specs/${specInfo.specFilename}`;
    initContext.specName = specInfo.derivedName;
  }

  initContext.theme = options.theme || 'corporate';

  const stackwrightDir = path.join(targetDir, '.stackwright');
  await fs.ensureDir(stackwrightDir);
  await fs.writeFile(
    path.join(stackwrightDir, 'init-context.json'),
    JSON.stringify(initContext, null, 2) + '\n',
    'utf-8'
  );

  // ------------------------------------------------------------------
  // 5c. Scaffold .code_puppy/ workspace directory
  //     Creates config.json (projectOnly: true), mcp_servers.json,
  //     and agents/ dir. raft-puppy discovers this via walk-up from cwd
  //     on first startup — no CLI flags needed.
  // ------------------------------------------------------------------

  await scaffoldWorkspaceConfig(targetDir);
  console.log(chalk.green('🐾 .code_puppy/ workspace scaffolded (projectOnly: true)'));

  // ------------------------------------------------------------------
  // 6. Print next steps
  // ------------------------------------------------------------------

  const relDir = path.relative(process.cwd(), targetDir) || '.';

  console.log(chalk.cyan.bold("\n🎉 All set! Here's what to do next:\n"));
  console.log(chalk.white(`  1. cd ${relDir}`));
  console.log(chalk.white('  2. npx @stackwright-pro/raft'));
  console.log(chalk.dim('     (use --verbose for troubleshooting)'));

  if (specInfo) {
    console.log(chalk.cyan(`\n📡 Your API spec was copied to specs/${specInfo.specFilename}`));
    console.log(chalk.dim('   The prebuild will generate types & client on first `pnpm dev`.'));
  }

  if (options.mock) {
    console.log(chalk.cyan('\n🎭 Run with mock server:'));
    console.log(chalk.white('   pnpm dev:mock  # Starts Prism + Next.js'));
  }

  if (!options.skipOtters) {
    console.log(chalk.cyan.bold('\n🦦 The otter raft is ready to build your site!'));
    console.log(chalk.dim('   Run the command above to get started.\n'));
  }
}

// ---------------------------------------------------------------------------
// CLI definition
// ---------------------------------------------------------------------------

async function main(): Promise<void> {
  const program = new Command();

  program
    .name('launch-stackwright-pro')
    .description('🚢 Launch a new Stackwright Pro project with auth, OpenAPI, and the otter raft')
    .version(version)
    .argument('[directory]', 'Project directory', '.')
    .option('--name <name>', 'Project name (used in package.json)')
    .option('--title <title>', 'Site title shown in the app bar and browser tab')
    .option('--theme <themeId>', 'Theme ID (e.g., corporate, creative, minimal)')
    .option('--force', 'Launch even if the target directory is not empty')
    .option('--skip-otters', 'Skip copying otter raft configs')
    .option('-y, --yes', 'Skip all prompts, use defaults')
    .option('--mock', 'Configure Prism mock server for API development (runs on port 4010)')
    .option(
      '--spec <paths...>',
      'Paths to OpenAPI specs (YAML or JSON) — copies into project and wires up integrations (can be specified multiple times)'
    )
    .option(
      '--spec-name <name>',
      'Name for the API integration (default: derived from spec filename)'
    )
    .option(
      '--skip-install',
      'Skip automatic pnpm install (useful in CI or when using a different package manager)'
    )
    .action(async (directory: string, options: LaunchProOptions) => {
      const homeDir = os.homedir();
      const targetDir =
        directory !== '.'
          ? path.resolve(process.cwd(), directory)
          : options.name
            ? path.resolve(process.cwd(), options.name)
            : process.cwd(); // last resort — same as old behaviour, only hit if neither arg nor --name

      // Warn (don't block) when scaffolding outside home dir — unusual for a user-facing CLI
      if (!targetDir.startsWith(homeDir + path.sep) && targetDir !== homeDir) {
        console.warn(chalk.yellow(`⚠️  Scaffolding outside your home directory: ${targetDir}`));
      }

      await launch(targetDir, options);
    });

  await program.parseAsync(process.argv);
}

main().catch((err: unknown) => {
  console.error(chalk.red('\n❌ Launch failed:'), err);
  process.exit(1);
});
